import * as ApartmentTypesActions from "./apartment-types.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {ApartmentTypes, ApartmentTypesSearchParams, ApartmentTypesType} from '@association/_common/models/apartments.models';

export const apartmentTypesFeatureKey = 'apartmentTypes';

export function sortApartments(a: ApartmentTypes, b: ApartmentTypes): number {
    const getOrder = (type: ApartmentTypesType) =>
        ({APARTMENT: 1, PRIVATE_BUILDING: 2, PARKING_PLACE: 3, COMMERCIAL: 4, OTHER: 8}[type] || 8);

    const compare = (valA: string | undefined, valB: string | undefined) =>
        (valA || '').localeCompare(valB || '', undefined, {numeric: true});

    return (
        getOrder(a.type || 'OTHER') - getOrder(b.type || 'OTHER') ||
        compare(a.name, b.name)
    );
}

export const adapter: EntityAdapter<ApartmentTypes> = createEntityAdapter<ApartmentTypes>({
    sortComparer: sortApartments
});

export type ApartmentTypesState = EntityStateModel<ApartmentTypes, any>;

export const initialState: ApartmentTypesState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const apartmentTypesReducer = createReducer(
    initialState,

    on(ApartmentTypesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: action.payload.loading
    })),

    on(ApartmentTypesActions.LoadListSuccess, (state, action) => {
        return adapter.setAll(action.payload.items, {
            ...state,
            pagination: action.payload.pagination,
            listLoading: undefined
        });
    }),

    on(ApartmentTypesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(ApartmentTypesActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(ApartmentTypesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(ApartmentTypesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(ApartmentTypesActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(ApartmentTypesActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(ApartmentTypesActions.DeleteItemSuccess, (state, action) => {
        const item = {...action.payload.item, archived: !action.payload.deleted};
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, item);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(item, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.item.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state.pagination, state?.ids)}
            });
        }
        return adapter.removeOne(action.payload.item.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.item.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(ApartmentTypesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(ApartmentTypesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: ApartmentTypesSearchParams | undefined, item: ApartmentTypes): boolean => {
    if (!searchParams) {
        return true;
    }

    if (!searchParams?.includeDeleted && item.archived) {
        return false;
    }

    if (searchParams.query && !item.name?.toLowerCase()?.includes(searchParams.query.toLowerCase()) && !item.shortName?.toLowerCase()?.includes(searchParams.query.toLowerCase())) {
        return false;
    }

    return true;
};
